import * as React from 'react';
import { Button, Form } from 'react-bootstrap';
import { getLocalization, globalWindow } from 'global/global';
import { useAppDispatch, useAppSelector } from 'index';
import { showAlert, showConfirm } from 'actions';
import { deleteAdminUsers, saveAdminUser } from 'actions/adminUserActions';
import { saveAdminGroup } from 'actions/adminGroupActions';
import { exportUsersUrl } from 'api/adminApi';
import { adminGroupsSelector, adminUsersSelector } from 'reducers/admin/selectors';
import { setGridSelection, setGridShow, showColumns, showDetail } from 'reducers/admin/ui';
import { showDeleteGroup } from '../modals/DeleteGroupModal';
import { showSendEmail } from '../modals/SendEmailModal';
import { campaignColumns, groupColumns, userColumns } from './columns';
import DataTable from './DataTable';
import { checkGroupEdit, checkUserDeletion, checkUserEdit, isGroupDeactivation } from './editRules';
import { buildCampaignRows, buildGroupRows, buildUserRows, scopeGroupId } from './rows';

/**
 *  The users / groups / campaigns grid, replacing DetailsView.js and the
 *  jqxGrid it was built on.
 */
export default function AdminGrid() {
  const dispatch = useAppDispatch();
  const groups = useAppSelector(adminGroupsSelector);
  const users = useAppSelector(adminUsersSelector);
  const campaigns = useAppSelector((state) => state.admin.campaigns.collection);
  const hierarchies = useAppSelector((state) => state.admin.meta.hierarchies);
  const account = useAppSelector((state) => state.admin.account);
  const ui = useAppSelector((state) => state.admin.ui);

  const { gridMode: mode, gridShow: show, gridSelection: selection } = ui;

  /** Scoped views are relative to the selected group. */
  const selectedGroupId = ui.path.length > 0 && ui.selected?.type === 'group'
    ? ui.selected.id
    : null;
  const groupId = scopeGroupId(selectedGroupId, account.groupId);

  const ctx = { groups, users, hierarchies };
  const setSelection = (ids: number[]) => dispatch(setGridSelection(ids));

  const refuse = (message: string) => {
    dispatch(showAlert({ visible: true, message: getLocalization(message) }));
  };

  const onUserEdit = (row: { id: number }, field: string, value: string | boolean) => {
    const user = users.find((u) => u.id === row.id);
    if (!user) {
      return;
    }
    const next = { ...user, [field]: value };
    const refusal = checkUserEdit(user, { active: next.active }, account);
    if (refusal) {
      refuse(refusal);
      return;
    }
    void dispatch(saveAdminUser(next));
  };

  const onGroupEdit = (row: { id: number }, field: string, value: string | boolean) => {
    const group = groups.find((g) => g.id === row.id);
    if (!group) {
      return;
    }
    const next = { ...group, [field]: value, added_by: account.groupId };
    const refusal = checkGroupEdit(group, { active: next.active }, account);
    if (refusal) {
      refuse(refusal);
      return;
    }
    // Switching a group off cascades to its data, so it is always confirmed.
    if (isGroupDeactivation(group, { active: next.active })) {
      dispatch(showConfirm({
        visible: true,
        message: getLocalization('groupDeactivateConfirm'),
        onConfirm: () => { void dispatch(saveAdminGroup(next, group.active)); },
      }));
      return;
    }
    void dispatch(saveAdminGroup(next, group.active));
  };

  const onDelete = () => {
    if (selection.length === 0) {
      return;
    }
    if (mode === 'groups') {
      showDeleteGroup(selection);
      return;
    }
    const refusal = checkUserDeletion(users, selection, account);
    if (refusal) {
      refuse(refusal);
      return;
    }
    dispatch(showConfirm({
      visible: true,
      message: `${getLocalization('deleteAlert')} ${getLocalization('deleteUser')}`,
      onConfirm: () => {
        void dispatch(deleteAdminUsers(selection)).then(() => setSelection([]));
      },
    }));
  };

  const onExport = () => {
    globalWindow.open(
      exportUsersUrl(selection, globalWindow.userName, globalWindow.pwrd), '_blank',
    );
  };

  const onSendEmail = () => {
    const emails = Array.from(new Set(
      selection
        .map((id) => users.find((u) => u.id === id)?.email?.toLowerCase())
        .filter((email): email is string => !!email),
    ));
    showSendEmail(emails.join(';'));
  };

  const open = (kind: 'group' | 'user' | 'campaign') => (id: number) =>
    dispatch(showDetail({ kind, id }));

  const actions = (
    <>
      <Button size="sm" variant="danger" disabled={selection.length === 0} onClick={onDelete}>
        {getLocalization('deleteStr')}
      </Button>
      {mode === 'users' && (
        <>
          <Button size="sm" disabled={selection.length === 0} onClick={onSendEmail}>
            {getLocalization('sendEmail')}
          </Button>
          <Button size="sm" disabled={selection.length === 0} onClick={onExport}>
            {getLocalization('exportUsers')}
          </Button>
        </>
      )}
    </>
  );

  const close = () => dispatch(showColumns({ path: ui.path }));

  if (mode === 'campaigns') {
    return (
      <DataTable
        data={buildCampaignRows(campaigns, hierarchies)}
        columns={campaignColumns({ onOpen: open('campaign') })}
        selection={selection}
        onSelectionChange={setSelection}
        onEdit={() => undefined}
        title={getLocalization('viewCampaigns')}
        actions={actions}
        onClose={close}
      />
    );
  }

  if (mode === 'groups') {
    return (
      <DataTable
        data={buildGroupRows(ctx, show, groupId)}
        columns={groupColumns({ onOpen: open('group') })}
        selection={selection}
        onSelectionChange={setSelection}
        onEdit={onGroupEdit}
        title={getLocalization('allGroups')}
        actions={actions}
        onClose={close}
      />
    );
  }

  return (
    <>
      {show !== 'all' && (
        <Form.Check
          id="grid-subgroup-users"
          label={getLocalization('subgroupUsers')}
          checked={show === 'groupSubgroupUsers'}
          onChange={(e) => dispatch(setGridShow(e.target.checked ? 'groupSubgroupUsers' : 'groupUsers'))}
        />
      )}
      <DataTable
        data={buildUserRows(ctx, show, groupId)}
        columns={userColumns({ onOpen: open('user') })}
        selection={selection}
        onSelectionChange={setSelection}
        onEdit={onUserEdit}
        title={getLocalization('allUsers')}
        actions={actions}
        onClose={close}
      />
    </>
  );
}
